import React, { useState, useEffect, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { Message } from 'primereact/message';
import { Toast } from 'primereact/toast';
import { Calendar } from 'primereact/calendar';
import { GlobalDropdownField, DropdownOption } from '../../../components/GlobalComponents/GlobalDropdownField';
import apiClient from '../../../stateManagers/apiSetup/axiosInterceptor';
import { useBatchStore } from '../../../stateManagers/stores/batchStore';
import { useCourseStore } from '../../../stateManagers/stores/courseStore';

const C = {
  navy:'#0e225d',navyMid:'#1a3370',navyDark:'#0a1a3d',
  gold:'#fbb21f',goldLight:'#ffc93c',
  goldDim:'rgba(251,178,31,0.18)',goldBorder:'rgba(251,178,31,0.35)',
  white:'#ffffff',border:'#E5E7EB',bg:'#F5F7FA',
  muted:'#9ca3af',sub:'#6b7280',body:'#1f2937',
  red:'#dc2626',redLight:'#fef2f2',redBorder:'#fecaca',
  green:'#16a34a',greenLight:'#f0fdf4',greenBorder:'#bbf7d0',
};

interface SelectedStudent { student_id: string; name: string; }
interface ExistingEnrollment {
  enrollment_id: string; course_id: string|null; course_name: string|null;
  course_title: string|null; batch_id: string|null; batch_name: string|null;
  batch_time: string|null; date_of_admission: string|null;
}
interface StudentDetail {
  student_id: string; name: string; enrollments: ExistingEnrollment[]; total_courses: number;
}
interface StudentNewEnrollment {
  enabled: boolean; course_id: string; batch_id: string; date_of_admission: Date;
  conflictWarning: string|null; skipped: boolean;
}
interface EnrichedStudent {
  student_id: string; name: string; detail: StudentDetail|null;
  loading: boolean; newEnrollment: StudentNewEnrollment;
}
interface BulkEnrollCreated { index:number; student_id:string; enrollment_id:string; enrollment_no:string; }
interface BulkEnrollFailed  { index:number; student_id:string|null; error:string; }
interface BulkEnrollResponse {
  success:boolean; summary:{total:number;created:number;failed:number};
  created:BulkEnrollCreated[]; failed:BulkEnrollFailed[];
}
interface BulkDeleteFailed  { index:number; enrollment_id:string; error:string; }
interface BulkDeleteResponse {
  success:boolean; summary:{total:number;deleted:number;failed:number};
  deleted:{index:number;enrollment_id:string}[]; failed:BulkDeleteFailed[];
}
interface SubmitResult {
  enrollSummary:BulkEnrollResponse['summary']|null; deleteSummary:BulkDeleteResponse['summary']|null;
  enrollFailed:BulkEnrollFailed[]; deleteFailed:BulkDeleteFailed[]; enrolledNos:string[];
}
type Tab = 'enroll'|'deenroll';
interface Props {
  visible:boolean; students:SelectedStudent[]; instituteId?:string; branchId?:string;
  defaultTab?:Tab; onHide:()=>void; onSuccess?:()=>void;
}

const defaultNewEnrollment = (): StudentNewEnrollment => ({
  enabled:false, course_id:'', batch_id:'', date_of_admission:new Date(),
  conflictWarning:null, skipped:false,
});

const Checkbox = ({ checked, indeterminate=false, onChange, disabled=false, color=C.red }:
  { checked:boolean; indeterminate?:boolean; onChange:()=>void; disabled?:boolean; color?:string; }) => (
  <button onClick={e=>{e.stopPropagation();if(!disabled)onChange();}} disabled={disabled}
    style={{ width:'18px',height:'18px',flexShrink:0,borderRadius:'5px',
      cursor:disabled?'default':'pointer',
      border:checked||indeterminate?'none':`2px solid ${C.border}`,
      background:checked||indeterminate?color:C.white,
      display:'flex',alignItems:'center',justifyContent:'center',
      transition:'all 0.15s',boxShadow:checked||indeterminate?`0 2px 6px ${color}55`:'none',
      padding:0,opacity:disabled?0.4:1 }}>
    {indeterminate&&!checked&&<div style={{width:'8px',height:'2px',background:C.white,borderRadius:'1px'}}/>}
    {checked&&<i className="pi pi-check" style={{fontSize:'0.6rem',color:C.white}}/>}
  </button>
);

const FieldLabel = ({ label, required, children }:
  { label:string; required?:boolean; children:React.ReactNode }) => (
  <div style={{display:'flex',flexDirection:'column',gap:'6px'}}>
    <label style={{fontSize:'0.72rem',fontWeight:'700',color:C.muted,textTransform:'uppercase',letterSpacing:'0.5px'}}>
      {label}{required&&<span style={{color:C.red,marginLeft:'2px'}}>*</span>}
    </label>
    {children}
  </div>
);

const BulkManageEnrollmentsModal: React.FC<Props> = ({
  visible, students, instituteId, branchId, defaultTab='enroll', onHide, onSuccess,
}) => {
  const { courses, fetchCourses } = useCourseStore();
  const { batches, fetchBatches } = useBatchStore();
  const toastRef = useRef<Toast>(null);

  const [activeTab,    setActiveTab]    = useState<Tab>(defaultTab);
  const [enriched,     setEnriched]     = useState<EnrichedStudent[]>([]);
  const [fetching,     setFetching]     = useState(false);
  const [fetchError,   setFetchError]   = useState<string|null>(null);
  const [loading,      setLoading]      = useState(false);
  const [result,       setResult]       = useState<SubmitResult|null>(null);
  const [sharedCourseId, setSharedCourseId] = useState('');
  const [sharedBatchId,  setSharedBatchId]  = useState('');
  const [sharedDate,     setSharedDate]     = useState<Date>(new Date());
  const [deSelected,   setDeSelected]   = useState<Set<string>>(new Set());
  const [expanded,     setExpanded]     = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!visible||students.length===0) return;
    if (!courses) fetchCourses();
    if (!batches) fetchBatches();
    loadDetails();
  }, [visible]);

  useEffect(() => { if (visible) setActiveTab(defaultTab); }, [visible, defaultTab]);

  const loadDetails = async () => {
    setFetching(true); setFetchError(null); setResult(null);
    setDeSelected(new Set()); setSharedCourseId(''); setSharedBatchId(''); setSharedDate(new Date());
    setExpanded(new Set(students.map(s=>s.student_id)));
    setEnriched(students.map(s=>({...s,detail:null,loading:true,newEnrollment:defaultNewEnrollment()})));
    const results = await Promise.allSettled(students.map(s=>apiClient.get(`/v1/students/get-student/${s.student_id}`)));
    const updated: EnrichedStudent[] = students.map((s,idx) => {
      const r = results[idx];
      const detail = r.status==='fulfilled'&&r.value?.data?.success ? r.value.data.data as StudentDetail : null;
      return {...s,detail,loading:false,newEnrollment:defaultNewEnrollment()};
    });
    setEnriched(updated); setFetching(false);
    setDeSelected(new Set(updated.flatMap(e=>e.detail?.enrollments?.map(en=>en.enrollment_id)??[])));
    if (updated.every(e=>e.detail===null)) setFetchError('Failed to load student details. Please close and try again.');
  };

  const courseOptions: DropdownOption[] = (courses??[]).map(c=>({label:`${c.name} — ${c.title}`,value:c.course_id}));

  const getBatchOptions = (courseId:string): DropdownOption[] => {
    if (!courseId||!batches) return [];
    return batches.filter(b=>b.course_id===courseId&&b.is_active).map(b=>({label:`${b.batch_name} (${b.batch_time})`,value:b.batch_id}));
  };

  const formatDate = (d:Date): string => {
    if (!d||isNaN(d.getTime())) return '';
    return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`;
  };

  const getConflict = (studentId:string, courseId:string): string|null => {
    if (!courseId) return null;
    return enriched.find(e=>e.student_id===studentId)?.detail?.enrollments?.some(en=>en.course_id===courseId)
      ? 'Already enrolled in this course' : null;
  };

  const updateStudentEnroll = (studentId:string, patch:Partial<StudentNewEnrollment>) => {
    setEnriched(prev=>prev.map(e=>{
      if (e.student_id!==studentId) return e;
      const updated = {...e,newEnrollment:{...e.newEnrollment,...patch}};
      if (patch.course_id!==undefined) {
        const conflict = getConflict(studentId,patch.course_id);
        updated.newEnrollment.conflictWarning = conflict;
        updated.newEnrollment.skipped = !!conflict;
        if (patch.course_id!==e.newEnrollment.course_id) updated.newEnrollment.batch_id='';
      }
      return updated;
    }));
  };

  useEffect(() => {
    if (!sharedCourseId) return;
    setEnriched(prev=>prev.map(e=>{
      if (e.newEnrollment.enabled) return e;
      const conflict = getConflict(e.student_id,sharedCourseId);
      return {...e,newEnrollment:{...e.newEnrollment,conflictWarning:conflict,skipped:!!conflict}};
    }));
  }, [sharedCourseId]);

  const enrollCandidates = enriched.filter(e=>{
    const ne=e.newEnrollment, courseId=ne.enabled?ne.course_id:sharedCourseId, batchId=ne.enabled?ne.batch_id:sharedBatchId;
    return courseId&&batchId&&!ne.skipped;
  });
  const skippedStudents = enriched.filter(e=>!!( e.newEnrollment.enabled?e.newEnrollment.course_id:sharedCourseId)&&e.newEnrollment.skipped);
  const sharedBatchOptions = getBatchOptions(sharedCourseId);
  const allExistingIds     = enriched.flatMap(e=>e.detail?.enrollments?.map(en=>en.enrollment_id)??[]);
  const deSelectedCount    = deSelected.size;

  const toggleDeEnroll  = (id:string) => setDeSelected(prev=>{const n=new Set(prev);n.has(id)?n.delete(id):n.add(id);return n;});
  const toggleDeStudent = (sid:string) => {
    const ids=enriched.find(e=>e.student_id===sid)?.detail?.enrollments?.map(en=>en.enrollment_id)??[];
    const allChecked=ids.every(id=>deSelected.has(id));
    setDeSelected(prev=>{const n=new Set(prev);allChecked?ids.forEach(id=>n.delete(id)):ids.forEach(id=>n.add(id));return n;});
  };
  const toggleDeAll = () => setDeSelected(prev=>prev.size===allExistingIds.length?new Set():new Set(allExistingIds));
  const getDeStudentState = (sid:string):'all'|'some'|'none' => {
    const ids=enriched.find(e=>e.student_id===sid)?.detail?.enrollments?.map(en=>en.enrollment_id)??[];
    if (!ids.length) return 'none';
    const c=ids.filter(id=>deSelected.has(id)).length;
    return c===0?'none':c===ids.length?'all':'some';
  };
  const toggleExpand = (id:string) => setExpanded(prev=>{const n=new Set(prev);n.has(id)?n.delete(id):n.add(id);return n;});

  const handleSubmit = async () => {
    if (!instituteId||!branchId) { toastRef.current?.show({severity:'error',summary:'Missing Info',detail:'Institute or branch info missing',life:4000}); return; }
    if (activeTab==='enroll'&&enrollCandidates.length===0) { toastRef.current?.show({severity:'warn',summary:'Nothing to Enroll',detail:'No valid students to enroll.',life:4000}); return; }
    if (activeTab==='deenroll'&&deSelectedCount===0) { toastRef.current?.show({severity:'warn',summary:'Nothing Selected',detail:'Select at least one enrollment to remove.',life:4000}); return; }
    setLoading(true); setResult(null);
    try {
      let enrollResult:any=null, deleteResult:any=null;
      if (activeTab==='enroll'&&enrollCandidates.length>0) {
        const rows=enrollCandidates.map(e=>{const ne=e.newEnrollment;return{student_id:e.student_id,course_id:ne.enabled?ne.course_id:sharedCourseId,batch_id:ne.enabled?ne.batch_id:sharedBatchId,date_of_admission:formatDate(ne.enabled?ne.date_of_admission:sharedDate)};});
        const res=await apiClient.post<BulkEnrollResponse>('/v1/enrollments/bulk',{institute_id:instituteId,branch_id:branchId,enrollments:rows});
        enrollResult={summary:res.data.summary,failed:res.data.failed,nos:res.data.created.map((c:any)=>c.enrollment_no).filter(Boolean)};
      }
      if (activeTab==='deenroll'&&deSelectedCount>0) {
        const res=await apiClient.delete<BulkDeleteResponse>('/v1/enrollments/bulk',{data:{enrollment_ids:Array.from(deSelected)}});
        deleteResult={summary:res.data.summary,failed:res.data.failed};
      }
      const finalResult:SubmitResult={enrollSummary:enrollResult?.summary??null,deleteSummary:deleteResult?.summary??null,enrollFailed:enrollResult?.failed??[],deleteFailed:deleteResult?.failed??[],enrolledNos:enrollResult?.nos??[]};
      setResult(finalResult);
      const anySuccess=(finalResult.enrollSummary?.created??0)+(finalResult.deleteSummary?.deleted??0)>0;
      if (anySuccess) {
        const parts=[];
        if (finalResult.enrollSummary?.created) parts.push(`${finalResult.enrollSummary.created} enrolled`);
        if (finalResult.deleteSummary?.deleted) parts.push(`${finalResult.deleteSummary.deleted} removed`);
        toastRef.current?.show({severity:'success',summary:'Done',detail:parts.join(', '),life:4000});
        if (onSuccess) onSuccess();
        if ((finalResult.enrollFailed.length+finalResult.deleteFailed.length)===0) setTimeout(()=>handleClose(),2200);
      } else { toastRef.current?.show({severity:'error',summary:'Failed',detail:'No operations succeeded.',life:4000}); }
    } catch(err:any) {
      toastRef.current?.show({severity:'error',summary:'Error',detail:err.response?.data?.error||err.message||'Operation failed',life:5000});
    } finally { setLoading(false); }
  };

  const handleClose = () => {
    setEnriched([]); setFetchError(null); setResult(null);
    setDeSelected(new Set()); setSharedCourseId(''); setSharedBatchId('');
    setSharedDate(new Date()); setExpanded(new Set()); onHide();
  };

  const isDone = result!==null&&result.enrollFailed.length+result.deleteFailed.length===0;

  const TabBtn = ({tab,label,icon,count}:{tab:Tab;label:string;icon:string;count?:number}) => (
    <button onClick={()=>setActiveTab(tab)} style={{flex:1,padding:'10px 16px',border:'none',cursor:'pointer',borderRadius:'10px',fontSize:'0.82rem',fontWeight:'700',display:'flex',alignItems:'center',justifyContent:'center',gap:'6px',transition:'all 0.2s',
      background:activeTab===tab?(tab==='enroll'?C.gold:C.red):'transparent',
      color:activeTab===tab?(tab==='enroll'?C.navy:C.white):C.muted,
      boxShadow:activeTab===tab?'0 2px 8px rgba(0,0,0,0.15)':'none'}}>
      <i className={`pi ${icon}`} style={{fontSize:'0.82rem'}}/>
      {label}
      {count!==undefined&&count>0&&<span style={{padding:'1px 7px',borderRadius:'20px',fontSize:'0.65rem',fontWeight:'800',background:activeTab===tab?'rgba(255,255,255,0.25)':'rgba(14,34,93,0.08)',color:activeTab===tab?C.white:C.navy}}>{count}</span>}
    </button>
  );

  // ── Calendar input style shared ───────────────────────────────────────────
  const calInputStyle = (indent=38): React.CSSProperties => ({
    width:'100%', padding:`11px 12px 11px ${indent}px`,
    border:`1.5px solid ${C.border}`, borderRadius:'8px',
    fontSize:'0.9rem', outline:'none', background:C.white, color:C.body,
  });

  return (
    <>
      <Toast ref={toastRef} position="top-right"/>
      <Dialog visible={visible} onHide={handleClose}
        style={{width:'96vw',maxWidth:'920px'}}
        header={null} dismissableMask={!loading} draggable={false}
        pt={{
          root:{style:{borderRadius:'20px',overflow:'hidden',boxShadow:'0 25px 60px -12px rgba(14,34,93,0.35)'}},
          header:{style:{display:'none'}},
          content:{style:{padding:'0',background:C.bg,maxHeight:'92vh',overflowY:'auto'}},
        }}>
        <div style={{padding:'24px'}}>

          {/* ── Hero ─────────────────────────────────────────────────── */}
          <div style={{background:activeTab==='enroll'?`linear-gradient(180deg,${C.navy} 0%,${C.navyMid} 50%,${C.navyDark} 100%)`:'linear-gradient(180deg,#7f1d1d 0%,#991b1b 50%,#6b1212 100%)',borderRadius:'16px',padding:'22px 24px',marginBottom:'20px',color:C.white,position:'relative',overflow:'hidden',boxShadow:activeTab==='enroll'?'0 8px 24px rgba(14,34,93,0.25)':'0 8px 24px rgba(127,29,29,0.3)',transition:'all 0.3s'}}>
            <div style={{position:'absolute',top:'-40px',right:'-40px',width:'150px',height:'150px',background:'rgba(255,255,255,0.06)',borderRadius:'50%',filter:'blur(35px)',pointerEvents:'none'}}/>
            <div style={{position:'relative',zIndex:1,display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:'16px',flexWrap:'wrap'}}>
              <div>
                <div style={{display:'inline-flex',alignItems:'center',gap:'6px',padding:'3px 10px',background:'rgba(255,255,255,0.15)',border:'1px solid rgba(255,255,255,0.25)',borderRadius:'20px',marginBottom:'8px',fontSize:'0.62rem',fontWeight:'700',letterSpacing:'0.8px',textTransform:'uppercase'}}>
                  <i className="pi pi-users" style={{fontSize:'0.62rem'}}/> Bulk Enrollment Manager
                </div>
                <h2 style={{margin:'0 0 4px 0',fontSize:'1.3rem',fontWeight:'700',color:C.white}}>Manage Enrollments</h2>
                <p style={{margin:0,color:'rgba(255,255,255,0.65)',fontSize:'0.8rem'}}>{students.length} student{students.length>1?'s':''} selected</p>
              </div>
              <div style={{display:'flex',gap:'8px',flexWrap:'wrap'}}>
                {[{v:enrollCandidates.length,l:'to enroll'},{v:deSelectedCount,l:'to remove'}].map(({v,l})=>(
                  <div key={l} style={{padding:'6px 14px',background:'rgba(255,255,255,0.12)',borderRadius:'10px',textAlign:'center'}}>
                    <p style={{margin:'0 0 2px 0',fontSize:'1rem',fontWeight:'800',color:C.white}}>{v}</p>
                    <p style={{margin:0,fontSize:'0.6rem',color:'rgba(255,255,255,0.6)',textTransform:'uppercase',letterSpacing:'0.4px'}}>{l}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* ── Tabs ─────────────────────────────────────────────────── */}
          <div style={{display:'flex',gap:'6px',padding:'6px',background:C.white,borderRadius:'14px',border:`1px solid ${C.border}`,marginBottom:'20px',boxShadow:'0 1px 4px rgba(14,34,93,0.06)'}}>
            <TabBtn tab="enroll"   label="Enroll"    icon="pi-graduation-cap" count={enrollCandidates.length}/>
            <TabBtn tab="deenroll" label="De-Enroll" icon="pi-trash"          count={deSelectedCount}/>
          </div>

          {/* ── Fetch loading ─────────────────────────────────────────── */}
          {fetching&&(
            <div style={{padding:'18px',background:C.white,borderRadius:'14px',border:`1px solid ${C.border}`,marginBottom:'16px',display:'flex',alignItems:'center',gap:'12px'}}>
              <i className="pi pi-spin pi-spinner" style={{color:C.navy,fontSize:'1.1rem'}}/>
              <span style={{fontSize:'0.88rem',color:C.sub}}>Loading student enrollment data…</span>
            </div>
          )}
          {fetchError&&<Message severity="error" text={fetchError} style={{marginBottom:'16px',width:'100%'}}/>}

          {/* ── Result banner ─────────────────────────────────────────── */}
          {result&&(
            <div style={{marginBottom:'16px'}}>
              {result.enrollSummary&&(
                <div style={{padding:'12px 16px',background:result.enrollSummary.created>0?C.greenLight:C.redLight,border:`1px solid ${result.enrollSummary.created>0?C.greenBorder:C.redBorder}`,borderRadius:'10px',marginBottom:'8px',display:'flex',alignItems:'center',gap:'8px'}}>
                  <i className={`pi ${result.enrollSummary.created>0?'pi-check-circle':'pi-times-circle'}`} style={{color:result.enrollSummary.created>0?C.green:C.red,fontSize:'1rem'}}/>
                  <div style={{flex:1}}>
                    <span style={{fontWeight:'700',fontSize:'0.85rem',color:result.enrollSummary.created>0?'#166534':'#991b1b'}}>{result.enrollSummary.created} enrolled successfully</span>
                    {result.enrollSummary.failed>0&&<span style={{fontSize:'0.78rem',color:C.red,marginLeft:'8px'}}>{result.enrollSummary.failed} failed</span>}
                  </div>
                  {result.enrolledNos.length>0&&<div style={{fontSize:'0.7rem',color:'#166534',fontFamily:'monospace',textAlign:'right'}}>{result.enrolledNos.slice(0,3).join(', ')}{result.enrolledNos.length>3?` +${result.enrolledNos.length-3}`:''}</div>}
                </div>
              )}
              {result.deleteSummary&&(
                <div style={{padding:'12px 16px',background:result.deleteSummary.deleted>0?C.greenLight:C.redLight,border:`1px solid ${result.deleteSummary.deleted>0?C.greenBorder:C.redBorder}`,borderRadius:'10px',marginBottom:'8px',display:'flex',alignItems:'center',gap:'8px'}}>
                  <i className={`pi ${result.deleteSummary.deleted>0?'pi-check-circle':'pi-times-circle'}`} style={{color:result.deleteSummary.deleted>0?C.green:C.red,fontSize:'1rem'}}/>
                  <span style={{fontWeight:'700',fontSize:'0.85rem',color:result.deleteSummary.deleted>0?'#166534':'#991b1b'}}>{result.deleteSummary.deleted} de-enrolled successfully</span>
                  {result.deleteSummary.failed>0&&<span style={{fontSize:'0.78rem',color:C.red,marginLeft:'8px'}}>{result.deleteSummary.failed} failed</span>}
                </div>
              )}
              {[...result.enrollFailed,...result.deleteFailed].length>0&&(
                <div style={{padding:'10px 14px',background:C.redLight,border:`1px solid ${C.redBorder}`,borderRadius:'10px'}}>
                  <p style={{margin:'0 0 6px 0',fontSize:'0.7rem',fontWeight:'700',color:'#991b1b',textTransform:'uppercase'}}>Failures</p>
                  {result.enrollFailed.map((f,i)=><div key={`ef-${i}`} style={{fontSize:'0.78rem',color:'#7f1d1d',display:'flex',gap:'6px',marginTop:i>0?'4px':0}}><i className="pi pi-times-circle" style={{color:C.red,fontSize:'0.72rem',flexShrink:0,marginTop:'2px'}}/><span>Enroll row {f.index+1} ({f.student_id}): {f.error}</span></div>)}
                  {result.deleteFailed.map((f,i)=><div key={`df-${i}`} style={{fontSize:'0.78rem',color:'#7f1d1d',display:'flex',gap:'6px',marginTop:'4px'}}><i className="pi pi-times-circle" style={{color:C.red,fontSize:'0.72rem',flexShrink:0,marginTop:'2px'}}/><span>Remove {f.enrollment_id}: {f.error}</span></div>)}
                </div>
              )}
            </div>
          )}

          {/* ══════════════════════════════════════════════════════════ */}
          {/* ENROLL TAB                                                 */}
          {/* ══════════════════════════════════════════════════════════ */}
          {activeTab==='enroll'&&!fetching&&!fetchError&&(
            <div>
              {/* Shared section */}
              <div style={{background:C.white,borderRadius:'14px',border:`1px solid ${C.border}`,padding:'20px',marginBottom:'16px',boxShadow:'0 2px 8px rgba(14,34,93,0.04)'}}>
                <div style={{display:'flex',alignItems:'center',gap:'8px',marginBottom:'18px'}}>
                  <div style={{width:'4px',height:'18px',background:`linear-gradient(180deg,${C.gold} 0%,#e09a00 100%)`,borderRadius:'4px'}}/>
                  <span style={{fontSize:'0.78rem',fontWeight:'700',color:C.navy,textTransform:'uppercase',letterSpacing:'0.4px'}}>Shared Enrollment — applied to all students</span>
                </div>

                {/* ── STACKED fields ───────────────────────────────── */}
                <div style={{display:'flex',flexDirection:'column',gap:'14px'}}>
                  <GlobalDropdownField label="Course" required value={sharedCourseId}
                    onChange={val=>{setSharedCourseId(val);setSharedBatchId('');}}
                    options={courseOptions} placeholder="Select a course" icon="pi pi-book"/>

                  <GlobalDropdownField label="Batch" required value={sharedBatchId}
                    onChange={val=>setSharedBatchId(val)} options={sharedBatchOptions}
                    placeholder={sharedCourseId?'Select a batch':'Select a course first'}
                    icon="pi pi-users" disabled={!sharedCourseId||sharedBatchOptions.length===0}/>

                  {sharedCourseId&&sharedBatchOptions.length===0&&(
                    <div style={{padding:'8px 12px',background:'#fffbeb',border:`1px solid ${C.gold}`,borderRadius:'8px',fontSize:'0.78rem',color:'#92400e',display:'flex',alignItems:'center',gap:'6px'}}>
                      <i className="pi pi-info-circle" style={{color:C.gold}}/> No active batches available for this course
                    </div>
                  )}

                  <FieldLabel label="Admission Date" required>
                    <div style={{position:'relative',display:'flex',alignItems:'center'}}>
                      <i className="pi pi-calendar" style={{position:'absolute',left:'12px',color:C.muted,fontSize:'0.9rem',zIndex:1,pointerEvents:'none'}}/>
                      <Calendar value={sharedDate} onChange={e=>e.value&&setSharedDate(e.value as Date)}
                        dateFormat="dd/mm/yy" showIcon={false} style={{width:'100%'}}
                        inputStyle={calInputStyle(38)} maxDate={new Date()}/>
                    </div>
                  </FieldLabel>
                </div>
              </div>

              {/* Skipped warning */}
              {skippedStudents.length>0&&sharedCourseId&&(
                <div style={{padding:'10px 14px',background:'#fffbeb',border:`1px solid ${C.gold}`,borderRadius:'10px',marginBottom:'14px',display:'flex',gap:'8px',alignItems:'flex-start'}}>
                  <i className="pi pi-exclamation-triangle" style={{color:C.gold,fontSize:'0.9rem',flexShrink:0,marginTop:'2px'}}/>
                  <div>
                    <p style={{margin:'0 0 3px 0',fontSize:'0.78rem',fontWeight:'700',color:'#92400e'}}>{skippedStudents.length} student{skippedStudents.length>1?'s':''} will be skipped</p>
                    <p style={{margin:0,fontSize:'0.72rem',color:'#b45309'}}>{skippedStudents.map(s=>s.name).join(', ')} — already enrolled in the selected course</p>
                  </div>
                </div>
              )}

              {/* Per-student list */}
              <div style={{background:C.white,borderRadius:'14px',border:`1px solid ${C.border}`,overflow:'hidden'}}>
                <div style={{padding:'10px 16px',background:'#fafafa',borderBottom:`1px solid ${C.border}`,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                  <span style={{fontSize:'0.72rem',fontWeight:'700',color:C.muted,textTransform:'uppercase',letterSpacing:'0.5px'}}>Students</span>
                  <span style={{padding:'2px 10px',borderRadius:'20px',fontSize:'0.7rem',fontWeight:'700',background:enrollCandidates.length>0?C.goldDim:'rgba(156,163,175,0.1)',color:enrollCandidates.length>0?'#b8860b':C.muted,border:`1px solid ${enrollCandidates.length>0?C.goldBorder:C.border}`}}>
                    {enrollCandidates.length} / {students.length} will be enrolled
                  </span>
                </div>

                <div style={{maxHeight:'380px',overflowY:'auto'}}>
                  {enriched.map((e,i)=>{
                    const ne=e.newEnrollment, isSkipped=ne.skipped, isExpanded=expanded.has(e.student_id);
                    const existingCount=e.detail?.enrollments?.length??0, isLast=i===enriched.length-1;
                    const overrideBatches=getBatchOptions(ne.course_id);
                    return (
                      <div key={e.student_id} style={{borderBottom:isLast?'none':`1px solid ${C.border}`}}>
                        {/* Student row */}
                        <div style={{padding:'12px 16px',display:'flex',alignItems:'center',gap:'10px',background:isSkipped?'#fffbeb':ne.enabled?'#f0f9ff':C.white,transition:'background 0.15s'}}>
                          <button onClick={()=>toggleExpand(e.student_id)} style={{background:'none',border:'none',cursor:'pointer',padding:'2px',color:C.muted,display:'flex',alignItems:'center',transition:'transform 0.2s',transform:isExpanded?'rotate(90deg)':'rotate(0deg)',flexShrink:0}}>
                            <i className="pi pi-chevron-right" style={{fontSize:'0.7rem'}}/>
                          </button>
                          <div style={{width:'34px',height:'34px',borderRadius:'50%',flexShrink:0,background:isSkipped?'rgba(251,178,31,0.15)':ne.enabled?'rgba(14,34,93,0.08)':'rgba(22,163,74,0.1)',display:'flex',alignItems:'center',justifyContent:'center'}}>
                            <i className="pi pi-user" style={{fontSize:'0.75rem',color:isSkipped?C.gold:ne.enabled?C.navy:C.green}}/>
                          </div>
                          <div style={{flex:1,minWidth:0}}>
                            <div style={{display:'flex',alignItems:'center',gap:'6px',flexWrap:'wrap'}}>
                              <p style={{margin:0,fontSize:'0.85rem',fontWeight:'600',color:isSkipped?'#92400e':C.body}}>{e.name}</p>
                              {isSkipped&&<span style={{padding:'1px 8px',borderRadius:'20px',fontSize:'0.62rem',fontWeight:'700',background:'rgba(251,178,31,0.2)',color:'#92400e',border:`1px solid ${C.goldBorder}`}}>⚠ Already enrolled — skipped</span>}
                              {!isSkipped&&(ne.enabled?ne.course_id&&ne.batch_id:sharedCourseId&&sharedBatchId)&&<span style={{padding:'1px 8px',borderRadius:'20px',fontSize:'0.62rem',fontWeight:'700',background:'rgba(22,163,74,0.1)',color:C.green,border:'1px solid rgba(22,163,74,0.25)'}}>✓ Will be enrolled</span>}
                              {ne.enabled&&<span style={{padding:'1px 8px',borderRadius:'20px',fontSize:'0.62rem',fontWeight:'700',background:'rgba(14,34,93,0.08)',color:C.navy,border:'1px solid rgba(14,34,93,0.15)'}}>Custom override</span>}
                            </div>
                            <p style={{margin:'2px 0 0 0',fontSize:'0.68rem',color:C.muted}}>{existingCount} existing enrollment{existingCount!==1?'s':''}</p>
                          </div>
                          {!isSkipped&&(
                            <button onClick={()=>updateStudentEnroll(e.student_id,{enabled:!ne.enabled,course_id:'',batch_id:'',conflictWarning:null,skipped:false})}
                              style={{padding:'4px 10px',borderRadius:'8px',fontSize:'0.7rem',fontWeight:'600',cursor:'pointer',transition:'all 0.2s',border:`1px solid ${ne.enabled?C.navy:C.border}`,background:ne.enabled?C.navy:'transparent',color:ne.enabled?C.white:C.muted,whiteSpace:'nowrap',flexShrink:0}}>
                              {ne.enabled?'✕ Remove override':'⊕ Override'}
                            </button>
                          )}
                        </div>

                        {/* Expanded panel */}
                        {isExpanded&&(
                          <div style={{background:'#fafafa',borderTop:`1px solid ${C.border}`}}>
                            {/* Override fields — STACKED */}
                            {ne.enabled&&(
                              <div style={{padding:'16px 16px 16px 60px',background:'#f0f9ff',borderBottom:'1px solid #bae6fd'}}>
                                <p style={{margin:'0 0 12px 0',fontSize:'0.72rem',fontWeight:'700',color:C.navy,textTransform:'uppercase',letterSpacing:'0.4px'}}>Custom Enrollment for {e.name}</p>
                                <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
                                  <GlobalDropdownField label="Course" value={ne.course_id}
                                    onChange={val=>updateStudentEnroll(e.student_id,{course_id:val})}
                                    options={courseOptions} placeholder="Select a course" icon="pi pi-book"
                                    error={ne.conflictWarning??undefined}/>
                                  <GlobalDropdownField label="Batch" value={ne.batch_id}
                                    onChange={val=>updateStudentEnroll(e.student_id,{batch_id:val})}
                                    options={overrideBatches}
                                    placeholder={ne.course_id?'Select a batch':'Select a course first'}
                                    icon="pi pi-users" disabled={!ne.course_id||overrideBatches.length===0}/>
                                  <FieldLabel label="Admission Date">
                                    <div style={{position:'relative',display:'flex',alignItems:'center'}}>
                                      <i className="pi pi-calendar" style={{position:'absolute',left:'12px',color:C.muted,fontSize:'0.85rem',zIndex:1,pointerEvents:'none'}}/>
                                      <Calendar value={ne.date_of_admission}
                                        onChange={ev=>ev.value&&updateStudentEnroll(e.student_id,{date_of_admission:ev.value as Date})}
                                        dateFormat="dd/mm/yy" showIcon={false} style={{width:'100%'}}
                                        inputStyle={calInputStyle(38)} maxDate={new Date()}/>
                                    </div>
                                  </FieldLabel>
                                  {ne.conflictWarning&&(
                                    <div style={{padding:'7px 10px',background:'#fffbeb',border:`1px solid ${C.gold}`,borderRadius:'8px',fontSize:'0.75rem',color:'#92400e',display:'flex',gap:'6px',alignItems:'center'}}>
                                      <i className="pi pi-exclamation-triangle" style={{color:C.gold}}/> {ne.conflictWarning}
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                            {/* Existing enrollments */}
                            {existingCount>0?(
                              <div>
                                <p style={{margin:0,padding:'8px 16px 6px 60px',fontSize:'0.65rem',fontWeight:'700',color:C.muted,textTransform:'uppercase',letterSpacing:'0.5px',borderBottom:`1px solid ${C.border}`}}>Current Enrollments</p>
                                {e.detail!.enrollments.map((en,ci)=>(
                                  <div key={en.enrollment_id} style={{padding:'8px 16px 8px 60px',borderBottom:ci<e.detail!.enrollments.length-1?`1px solid ${C.border}`:'none',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                                    <div style={{minWidth:0}}>
                                      <p style={{margin:'0 0 2px 0',fontSize:'0.78rem',fontWeight:'600',color:C.body,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{en.course_name}{en.course_title?` — ${en.course_title}`:''}</p>
                                      <p style={{margin:0,fontSize:'0.68rem',color:C.muted}}>{[en.batch_name,en.batch_time,en.date_of_admission?`Admitted ${en.date_of_admission}`:null].filter(Boolean).join(' · ')}</p>
                                    </div>
                                    <span style={{fontSize:'0.62rem',fontFamily:'monospace',color:C.muted,opacity:0.6,flexShrink:0,marginLeft:'8px'}}>#{en.enrollment_id.slice(-6)}</span>
                                  </div>
                                ))}
                              </div>
                            ):(
                              <p style={{margin:0,padding:'10px 16px 10px 60px',fontSize:'0.78rem',color:C.muted,fontStyle:'italic'}}>No existing enrollments</p>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {enrollCandidates.length>0&&!result&&(
                <div style={{marginTop:'14px',padding:'12px 14px',background:C.goldDim,border:`1px solid ${C.goldBorder}`,borderRadius:'10px',display:'flex',gap:'8px',alignItems:'center'}}>
                  <i className="pi pi-info-circle" style={{color:C.gold,fontSize:'0.9rem',flexShrink:0}}/>
                  <p style={{margin:0,fontSize:'0.78rem',color:'#7d5a00'}}><strong>{enrollCandidates.length}</strong> student{enrollCandidates.length>1?'s':''} will be enrolled{skippedStudents.length>0?`. ${skippedStudents.length} skipped due to existing enrollment.`:'.'}</p>
                </div>
              )}
            </div>
          )}

          {/* ══════════════════════════════════════════════════════════ */}
          {/* DE-ENROLL TAB                                              */}
          {/* ══════════════════════════════════════════════════════════ */}
          {activeTab==='deenroll'&&!fetching&&!fetchError&&(
            <div>
              <div style={{background:C.white,borderRadius:'14px',border:`1px solid ${C.border}`,overflow:'hidden'}}>
                {/* Select-all header */}
                <div onClick={allExistingIds.length>0?toggleDeAll:undefined}
                  style={{padding:'12px 16px',background:'#fafafa',borderBottom:`1px solid ${C.border}`,display:'flex',justifyContent:'space-between',alignItems:'center',cursor:allExistingIds.length>0?'pointer':'default',userSelect:'none'}}
                  onMouseEnter={ev=>{if(allExistingIds.length>0)ev.currentTarget.style.background='#f3f4f6';}}
                  onMouseLeave={ev=>{ev.currentTarget.style.background='#fafafa';}}>
                  <div style={{display:'flex',alignItems:'center',gap:'10px'}}>
                    <Checkbox checked={deSelected.size===allExistingIds.length&&allExistingIds.length>0} indeterminate={deSelected.size>0&&deSelected.size<allExistingIds.length} onChange={toggleDeAll} disabled={allExistingIds.length===0}/>
                    <span style={{fontSize:'0.72rem',fontWeight:'700',color:C.navy,textTransform:'uppercase',letterSpacing:'0.5px'}}>Select All Enrollments</span>
                  </div>
                  <span style={{padding:'2px 10px',borderRadius:'20px',fontSize:'0.7rem',fontWeight:'700',background:deSelectedCount>0?'rgba(220,38,38,0.1)':'rgba(156,163,175,0.1)',color:deSelectedCount>0?C.red:C.muted,border:`1px solid ${deSelectedCount>0?'rgba(220,38,38,0.25)':C.border}`,transition:'all 0.2s'}}>
                    {deSelectedCount} / {allExistingIds.length} selected
                  </span>
                </div>

                <div style={{maxHeight:'420px',overflowY:'auto'}}>
                  {enriched.map((e,i)=>{
                    const courseCount=e.detail?.enrollments?.length??0, hasEnroll=courseCount>0;
                    const checkState=getDeStudentState(e.student_id), isLast=i===enriched.length-1;
                    return (
                      <div key={e.student_id} style={{borderBottom:isLast?'none':`1px solid ${C.border}`}}>
                        <div onClick={()=>hasEnroll&&toggleDeStudent(e.student_id)}
                          style={{padding:'12px 16px',display:'flex',justifyContent:'space-between',alignItems:'center',background:hasEnroll?C.white:'#fafafa',cursor:hasEnroll?'pointer':'default',userSelect:'none',transition:'background 0.15s'}}
                          onMouseEnter={ev=>{if(hasEnroll)ev.currentTarget.style.background='#fafafa';}}
                          onMouseLeave={ev=>{if(hasEnroll)ev.currentTarget.style.background=C.white;}}>
                          <div style={{display:'flex',alignItems:'center',gap:'10px'}}>
                            <Checkbox checked={checkState==='all'} indeterminate={checkState==='some'} onChange={()=>toggleDeStudent(e.student_id)} disabled={!hasEnroll}/>
                            <div style={{width:'34px',height:'34px',borderRadius:'50%',flexShrink:0,background:hasEnroll?'rgba(220,38,38,0.08)':'rgba(156,163,175,0.1)',display:'flex',alignItems:'center',justifyContent:'center'}}>
                              <i className="pi pi-user" style={{fontSize:'0.75rem',color:hasEnroll?C.red:C.muted}}/>
                            </div>
                            <div>
                              <p style={{margin:0,fontSize:'0.85rem',fontWeight:'600',color:hasEnroll?C.body:C.muted}}>{e.name}</p>
                              <p style={{margin:0,fontSize:'0.68rem',color:C.muted,fontFamily:'monospace'}}>{e.student_id}</p>
                            </div>
                          </div>
                          {e.loading?<i className="pi pi-spin pi-spinner" style={{color:C.muted,fontSize:'0.85rem'}}/>
                          :hasEnroll?<span style={{padding:'2px 10px',borderRadius:'20px',fontSize:'0.7rem',fontWeight:'700',background:checkState!=='none'?'rgba(220,38,38,0.1)':'rgba(156,163,175,0.1)',color:checkState!=='none'?C.red:C.muted,border:`1px solid ${checkState!=='none'?'rgba(220,38,38,0.2)':C.border}`,whiteSpace:'nowrap',transition:'all 0.2s'}}>{courseCount} course{courseCount>1?'s':''}</span>
                          :<span style={{padding:'2px 10px',borderRadius:'20px',fontSize:'0.7rem',fontWeight:'700',background:'rgba(156,163,175,0.1)',color:C.muted,border:`1px solid ${C.border}`,whiteSpace:'nowrap'}}>Not enrolled</span>}
                        </div>

                        {hasEnroll&&e.detail?.enrollments?.map((en,ci)=>{
                          const isChecked=deSelected.has(en.enrollment_id), isLastEn=ci===(e.detail?.enrollments?.length??1)-1;
                          return (
                            <div key={en.enrollment_id} onClick={()=>toggleDeEnroll(en.enrollment_id)}
                              style={{padding:'9px 16px 9px 56px',background:isChecked?'#fef2f2':'#fafafa',borderTop:`1px solid ${isChecked?'#fecaca':C.border}`,borderBottom:!isLastEn?`1px solid ${isChecked?'#fecaca':C.border}`:'none',display:'flex',alignItems:'center',gap:'10px',cursor:'pointer',userSelect:'none',transition:'background 0.15s'}}
                              onMouseEnter={ev=>{ev.currentTarget.style.background=isChecked?'#fee2e2':'#f3f4f6';}}
                              onMouseLeave={ev=>{ev.currentTarget.style.background=isChecked?'#fef2f2':'#fafafa';}}>
                              <Checkbox checked={isChecked} onChange={()=>toggleDeEnroll(en.enrollment_id)}/>
                              <div style={{flex:1,minWidth:0}}>
                                <p style={{margin:'0 0 2px 0',fontSize:'0.8rem',fontWeight:'600',color:isChecked?'#7f1d1d':C.body,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{en.course_name}{en.course_title?` — ${en.course_title}`:''}</p>
                                <p style={{margin:0,fontSize:'0.68rem',color:isChecked?'#991b1b':C.muted}}>{[en.batch_name,en.batch_time,en.date_of_admission?`Admitted ${en.date_of_admission}`:null].filter(Boolean).join(' · ')}</p>
                              </div>
                              <span style={{fontSize:'0.62rem',fontFamily:'monospace',color:isChecked?'#b91c1c':C.muted,flexShrink:0,opacity:0.7}}>#{en.enrollment_id.slice(-6)}</span>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              </div>

              {allExistingIds.length===0&&(
                <div style={{marginTop:'14px',padding:'20px',background:'#fffbeb',border:`2px solid ${C.gold}`,borderRadius:'14px',textAlign:'center'}}>
                  <i className="pi pi-info-circle" style={{fontSize:'1.4rem',color:C.gold,display:'block',marginBottom:'10px'}}/>
                  <p style={{margin:'0 0 4px 0',fontSize:'0.95rem',fontWeight:'700',color:C.navy}}>No Enrollments Found</p>
                  <p style={{margin:0,fontSize:'0.82rem',color:C.sub}}>None of the selected students are enrolled in any course.</p>
                </div>
              )}

              {deSelectedCount>0&&!result&&(
                <div style={{marginTop:'14px',padding:'12px 14px',background:'#fff7ed',border:'1px solid #fed7aa',borderRadius:'10px',display:'flex',gap:'8px',alignItems:'flex-start'}}>
                  <i className="pi pi-exclamation-triangle" style={{color:'#ea580c',fontSize:'0.9rem',flexShrink:0,marginTop:'2px'}}/>
                  <p style={{margin:0,fontSize:'0.78rem',color:'#c2410c'}}><strong>{deSelectedCount}</strong> enrollment{deSelectedCount>1?'s':''} will be permanently removed. This cannot be undone.</p>
                </div>
              )}
            </div>
          )}

          {/* ── Actions ──────────────────────────────────────────────── */}
          <div style={{display:'flex',gap:'12px',justifyContent:'flex-end',flexWrap:'wrap',marginTop:'20px'}}>
            <button onClick={handleClose} disabled={loading}
              style={{display:'inline-flex',alignItems:'center',gap:'8px',padding:'11px 24px',background:'transparent',border:`1.5px solid ${C.navy}`,borderRadius:'10px',color:C.navy,fontSize:'0.88rem',fontWeight:'600',cursor:loading?'not-allowed':'pointer',transition:'all 0.2s',opacity:loading?0.5:1}}
              onMouseEnter={e=>{if(!loading){e.currentTarget.style.background=C.navy;(e.currentTarget as HTMLButtonElement).style.color=C.white;}}}
              onMouseLeave={e=>{if(!loading){e.currentTarget.style.background='transparent';(e.currentTarget as HTMLButtonElement).style.color=C.navy;}}}>
              <i className="pi pi-times" style={{fontSize:'0.88rem'}}/>{isDone?'Close':'Cancel'}
            </button>

            {!isDone&&(
              <button onClick={handleSubmit}
                disabled={loading||fetching||!!fetchError||(activeTab==='enroll'&&enrollCandidates.length===0)||(activeTab==='deenroll'&&deSelectedCount===0)}
                style={{display:'inline-flex',alignItems:'center',gap:'8px',padding:'11px 28px',border:'none',borderRadius:'10px',fontSize:'0.88rem',fontWeight:'700',cursor:'pointer',transition:'all 0.2s',
                  background:loading||fetching||!!fetchError||(activeTab==='enroll'&&enrollCandidates.length===0)||(activeTab==='deenroll'&&deSelectedCount===0)?C.muted:activeTab==='enroll'?C.gold:C.red,
                  color:activeTab==='enroll'?C.navy:C.white,
                  boxShadow:activeTab==='enroll'?'0 4px 12px rgba(251,178,31,0.4)':'0 4px 12px rgba(220,38,38,0.35)'}}
                onMouseEnter={e=>{const ok=!loading&&!fetching&&!fetchError&&(activeTab==='enroll'?enrollCandidates.length>0:deSelectedCount>0);if(ok){e.currentTarget.style.transform='translateY(-1px)';e.currentTarget.style.filter='brightness(1.08)';}}}
                onMouseLeave={e=>{e.currentTarget.style.transform='translateY(0)';e.currentTarget.style.filter='none';}}>
                <i className={loading?'pi pi-spin pi-spinner':activeTab==='enroll'?'pi pi-check':'pi pi-trash'} style={{fontSize:'0.88rem'}}/>
                {loading?'Processing…':activeTab==='enroll'?`Enroll ${enrollCandidates.length} Student${enrollCandidates.length!==1?'s':''}`:` Remove ${deSelectedCount} Enrollment${deSelectedCount!==1?'s':''}`}
              </button>
            )}
          </div>

        </div>
      </Dialog>
    </>
  );
};

export default BulkManageEnrollmentsModal;